import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";
import SenderNumberTab from "./components/SenderNumberTab";
import AccountTab from "./components/AccountTab";
import ApiKeyTab from "./components/ApiKeyTab";
import NotificationTab from "./components/NotificationTab";
import CreditTab from "./components/CreditTab";

type TabId = "sender" | "account" | "credit" | "api" | "notification";

const TABS: { id: TabId; label: string; icon: string; description: string }[] = [
  { id: "sender", label: "발신번호", icon: "ri-phone-line", description: "발신번호 설정 안내" },
  { id: "account", label: "계정 정보", icon: "ri-user-settings-line", description: "프로필 및 보안 설정" },
  { id: "credit", label: "크레딧 내역", icon: "ri-wallet-3-line", description: "충전 및 사용 내역 조회" },
  { id: "api", label: "API 키", icon: "ri-key-2-line", description: "API 키 발급 및 관리" },
  { id: "notification", label: "알림 설정", icon: "ri-notification-3-line", description: "알림 채널 및 수신 설정" },
];

const PLAN_LABELS: Record<string, string> = {
  basic: "Basic 플랜",
  pro: "Pro 플랜",
  enterprise: "Enterprise 플랜",
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, loading, session } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : "account"
  );

  const [planName, setPlanName] = useState<string>("");
  const [planLoading, setPlanLoading] = useState(true);
  const [planToast, setPlanToast] = useState<string | null>(null);

  const [credit, setCredit] = useState<number>(0);
  const [creditLoading, setCreditLoading] = useState(true);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

  useEffect(() => {
    if (!user || !session) return;
    const fetchProfile = async () => {
      setPlanLoading(true);
      setCreditLoading(true);
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/get-user-profile`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ user_id: user.id }),
        });
        const data = await res.json();
        if (data.plan) {
          setPlanName(PLAN_LABELS[data.plan.toLowerCase()] || `${data.plan} 플랜`);
        } else {
          setPlanName(PLAN_LABELS.basic);
        }
        if (data.credit !== undefined && data.credit !== null) {
          setCredit(Number(data.credit));
        }
      } catch (err) {
        console.error("[Settings] profile fetch error:", err);
        setPlanName(PLAN_LABELS.basic);
      } finally {
        setPlanLoading(false);
        setCreditLoading(false);
      }
    };
    fetchProfile();
  }, [user, session?.access_token]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const handleTabChange = (id: TabId) => {
    setActiveTab(id);
    setSearchParams({ tab: id });
  };

  const handlePlanUpgrade = () => {
    setPlanToast("플랜 업그레이드는 현재 준비 중입니다.");
    setTimeout(() => setPlanToast(null), 2500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }
  if (!user) return null;

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";
  const currentTab = TABS.find((t) => t.id === activeTab)!;

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen pb-20 md:pb-0 min-w-0 overflow-x-hidden`}>
        {/* Top bar */}
        <header className="h-14 md:h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base md:text-lg"></i>
            </button>
            <div>
              <h1 className="text-white font-semibold text-sm md:text-base">설정</h1>
            </div>
          </div>
        </header>

        <div className="md:flex md:flex-row md:h-[calc(100vh-64px)] w-full overflow-hidden">
          {/* Desktop plan info - visible on mobile too, placed after header */}
          <div className="md:hidden px-3 pt-3 pb-1">
            <div className="flex items-center justify-between bg-white/[0.03] border border-white/[0.07] rounded-xl px-3 py-2.5">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-vip-crown-line text-violet-400 text-sm"></i>
                  </div>
                  <span className="text-violet-300 text-[11px] font-semibold whitespace-nowrap">
                    {planLoading ? "불러오는 중..." : planName}
                  </span>
                </div>
                <span className="text-gray-600 text-[10px] shrink-0">|</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-wallet-3-line text-emerald-400 text-sm"></i>
                  </div>
                  <span className="text-emerald-300 text-[11px] font-semibold whitespace-nowrap">
                    {creditLoading ? "..." : `${credit.toLocaleString()} 크레딧`}
                  </span>
                </div>
              </div>
              <button
                onClick={handlePlanUpgrade}
                className="px-2.5 py-1 rounded-lg bg-gradient-to-r from-violet-600/50 to-pink-600/50 text-white text-[10px] font-semibold cursor-pointer whitespace-nowrap transition-all hover:from-violet-600/70 hover:to-pink-600/70 shrink-0 ml-2"
              >
                업그레이드
              </button>
            </div>
          </div>

          {/* Mobile: horizontal tab scroller */}
          <div className="md:hidden px-3 pt-2 pb-2 overflow-x-auto tabs-scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex items-center gap-1.5 min-w-max">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all shrink-0 ${
                      isActive
                        ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                        : "bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white hover:border-white/[0.15]"
                    }`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${tab.icon} text-[13px]`}></i>
                    </div>
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Desktop: left sidebar tabs */}
          <aside className="hidden md:flex w-56 shrink-0 border-r border-white/[0.06] bg-[#0a0a18]/40 p-4 flex-col gap-1">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl text-left cursor-pointer whitespace-nowrap transition-all w-full ${
                    isActive
                      ? "bg-gradient-to-r from-violet-600/25 to-pink-600/15 border border-violet-500/20 text-white"
                      : "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 bg-white/[0.05]">
                    <i className={`${tab.icon} text-base ${isActive ? "text-violet-400" : "text-current"}`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{tab.label}</p>
                  </div>
                  {isActive && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gradient-to-r from-violet-400 to-pink-400 shrink-0"></span>
                  )}
                </button>
              );
            })}

            <div className="my-2 h-px bg-white/[0.06]"></div>

            <div className="bg-gradient-to-br from-violet-500/10 to-pink-500/10 border border-violet-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-violet-400 text-sm"></i>
                </div>
                <span className="text-violet-300 text-xs font-semibold">
                  {planLoading ? "불러오는 중..." : planName}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-wallet-3-line text-emerald-400 text-sm"></i>
                </div>
                <span className="text-emerald-300 text-xs font-semibold">
                  {creditLoading ? "불러오는 중..." : `잔여 ${credit.toLocaleString()} 크레딧`}
                </span>
              </div>
              <p className="text-gray-600 text-xs mb-2.5">현재 이용 중인 요금제</p>
              <button
                onClick={handlePlanUpgrade}
                className="w-full py-1.5 rounded-lg bg-gradient-to-r from-violet-600/50 to-pink-600/50 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all hover:from-violet-600/70 hover:to-pink-600/70"
              >
                플랜 업그레이드
              </button>
            </div>
          </aside>

          {/* Main content */}
          <div className="md:flex-1 md:overflow-y-auto md:min-h-0 px-3 py-4 md:p-8 overflow-hidden">
            <div className="max-w-3xl mx-auto w-full min-w-0">
              <div className="flex items-center gap-2 mb-4 hidden md:flex">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05]">
                  <i className={`${currentTab.icon} text-violet-400 text-lg`}></i>
                </div>
                <div>
                  <h2 className="text-white font-semibold text-lg">{currentTab.label}</h2>
                  <p className="text-gray-500 text-sm">{currentTab.description}</p>
                </div>
              </div>
              {activeTab === "sender" && <SenderNumberTab />}
              {activeTab === "account" && <AccountTab />}
              {activeTab === "credit" && <CreditTab />}
              {activeTab === "api" && <ApiKeyTab />}
              {activeTab === "notification" && <NotificationTab />}
            </div>
          </div>
        </div>
      </main>

      <style>{`.tabs-scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>

      <MobileBottomNav />

      {/* Plan upgrade toast */}
      {planToast && (
        <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[60] flex items-center gap-2.5 px-4 py-3 rounded-xl border bg-amber-500/15 border-amber-500/30 text-amber-300">
          <i className="ri-information-line text-base"></i>
          <span className="text-sm font-medium">{planToast}</span>
        </div>
      )}
    </div>
  );
}